import { ModelBase } from '../../core/model/model-base';
import { Address } from '../../common/model/address';
import { LeaseRow } from './lease-row';
import { FormOfLease, FormOfLeaseToString, OrganizationOfTenantsKind, OrganizationOfTenantsKindToString, RentalObjectCategory } from './const/enums';
import { Contract } from './contract';
import { RentalObjectImprovement } from './rental-object-improvement';
import { VacancyPeriod } from './vacancy-period';
import { LocalDate } from '../../core/model/local-date';
import { RentApplication } from './rent-application';
import { Money } from '../../core/model/money';
import { FaultNotification } from '../../fault-notification/model/faultNotification';
import { LeaseAgreementTemplate } from './lease-agreement-template';
import { RentalObjectKey } from './rental-object-key';
import { BrfInnerFundTransaction } from './brf-inner-fund-transaction';
import { BrfMortgagePrescription } from './brf-mortgage-prescription';
import { Translation } from '../../core/model/translation';
import { RentalObjectRentalBlock } from './rental-object-rental-block';

export class RentalObject extends ModelBase {
  constructor() {
    super();
    this.address = new Address();
    this.leaseAgreementRowTemplates = [];
    this.leaseAgreements = [];
    this.improvements = [];
    this.vacancyPeriods = [];
    this.rentalBlocks = [];
    this.rentApplications = [];
    this.rentApplications = [];
    this.leaseAgreementTemplate = new LeaseAgreementTemplate();
    this.formOfLease = FormOfLease.Lease;
    Object.defineProperties(this, {
      _blockedFrom: { writable: true, enumerable: false },
      _blockedTo: { writable: true, enumerable: false },
    });
  }

  private _vacancyPeriods: VacancyPeriod[] = [];
  private _rentApplications: RentApplication[] = [];
  private _rentalBlocks: RentalObjectRentalBlock[] = [];
  private _leaseAgreementFromDate: LocalDate;
  private _leaseAgreementLastBillingDate: LocalDate;
  private _currentDuePeriodFrom: LocalDate;
  private _currentDuePeriodTo: LocalDate;
  private _blockedFrom: LocalDate;
  private _blockedTo: LocalDate;

  get blockedFrom(): LocalDate {
    return this._blockedFrom;
  }
  set blockedFrom(value: LocalDate) {
    this._blockedFrom = LocalDate.fromDate(value);
  }

  get blockedTo(): LocalDate {
    return this._blockedTo;
  }
  set blockedTo(value: LocalDate) {
    this._blockedTo = LocalDate.fromDate(value);
  }

  blockingReason: string = null;
  address: string | Address;
  buildingId: string;
  category: RentalObjectCategory;
  companyId: string;
  companyName: string;
  currencyCode: string;
  entranceId: string;
  formOfLease: FormOfLease;
  lettingFeatureReferenceTypes: Record<string, number[]> = {};
  leaseAgreementRowTemplates: LeaseRow[];
  leaseAgreements: Contract[];
  improvements: RentalObjectImprovement[];
  propertyId: string;
  rentalId: string;
  note: string;
  surface: number;
  rentalObjectTypeId: string;
  tenantOneName: string;
  monthlyRent: Money;
  currentMonthRentLeaseAgreement: Money;
  rentLossCurrentMonth: Money;
  monthlyRentNext: Money;
  nextMonthRentLeaseAgreement: Money;
  rentLossNextMonth: Money;
  currentPeriodText: string;
  rentalObjectDescription: Translation[];
  faultNotifications: FaultNotification[];
  leaseAgreementTemplate: LeaseAgreementTemplate;
  keys: RentalObjectKey[];
  brfInnerFundTransactions: BrfInnerFundTransaction[];
  brfMortgagePrescriptions: BrfMortgagePrescription[];
  propertyName: string;
  hasNegotiationProcedure: boolean;
  organizationOfTenants: OrganizationOfTenantsKind;

  get leaseAgreementFromDate(): LocalDate {
    return this._leaseAgreementFromDate;
  }
  set leaseAgreementFromDate(value: LocalDate) {
    this._leaseAgreementFromDate = LocalDate.fromDate(value);
  }
  get leaseAgreementLastBillingDate(): LocalDate {
    return this._leaseAgreementLastBillingDate;
  }
  set leaseAgreementLastBillingDate(value: LocalDate) {
    this._leaseAgreementLastBillingDate = LocalDate.fromDate(value);
  }

  get currentDuePeriodFrom(): LocalDate {
    return this._currentDuePeriodFrom;
  }
  set currentDuePeriodFrom(value: LocalDate) {
    this._currentDuePeriodFrom = LocalDate.fromDate(value);
  }
  get currentDuePeriodTo(): LocalDate {
    return this._currentDuePeriodTo;
  }
  set currentDuePeriodTo(value: LocalDate) {
    this._currentDuePeriodTo = LocalDate.fromDate(value);
  }

  get vacancyPeriods(): VacancyPeriod[] {
    return this._vacancyPeriods;
  }
  set vacancyPeriods(items: VacancyPeriod[]) {
    if (items instanceof Array) {
      items.forEach(item => {
        if (item instanceof VacancyPeriod) this._vacancyPeriods.push(item);
        else this._vacancyPeriods.push(Object.assign(new VacancyPeriod(), item));
      });
    }
  }

  get formOfLeaseText(): string {
    return FormOfLeaseToString(this.formOfLease);
  }

  get organizationOfTenantsText(): string {
    return OrganizationOfTenantsKindToString(this.organizationOfTenants);
  }

  get rentApplications(): RentApplication[] {
    return this._rentApplications;
  }
  set rentApplications(items: RentApplication[]) {
    if (items instanceof Array) {
      items.forEach(item => {
        if (item instanceof RentApplication) this._rentApplications.push(item);
        else this._rentApplications.push(Object.assign(new RentApplication(), item));
      });
    }
  }

  get rentalBlocks(): RentalObjectRentalBlock[] {
    return this._rentalBlocks;
  }
  set rentalBlocks(items: RentalObjectRentalBlock[]) {
    this._rentalBlocks = [];
    if (items instanceof Array) {
      items.forEach(item => {
        if (item instanceof RentalObjectRentalBlock) this._rentalBlocks.push(item);
        else this._rentalBlocks.push(Object.assign(new RentalObjectRentalBlock(), item));
      });
    }
  }

  toJSON() {
    let json = Object.assign({}, this);
    json.blockedFrom = this.blockedFrom;
    json.blockedTo = this.blockedTo;
    json.rentalBlocks = this.rentalBlocks;
    json.currentDuePeriodFrom = this.currentDuePeriodFrom;
    json.currentDuePeriodTo = this.currentDuePeriodTo;

    return json;
  }

  toString() {
    return `${this.rentalId}`;
  }

  toAutocompleteValue(viewType: 'list' | 'field', culture?: string): string {
    switch (viewType) {
      case 'field':
      case 'list':
        return this.toString();
    }
  }
}
